import {
  formatAge,
  formatCents,
  formatOneInChance,
  formatProbabilityPercent,
} from '../lib/format';
import type { OddsState } from '../hooks/useOdds';
import type { WinningValue } from '../lib/api';
import PendingValue from './PendingValue';

/**
 * Honest block odds -- the signature panel.
 *
 * The probability is the hero. A customer should learn their actual chance at a glance,
 * not by reading a small table.
 *
 * Every number is calculated by the server from live network difficulty. The browser
 * runs no Poisson maths and rounds nothing until this component formats it.
 *
 * The honesty rules this component enforces:
 *
 *   - A real but tiny probability is NEVER shown as "0%". `formatProbabilityPercent`
 *     writes "< 0.0001%" instead, because telling someone their chance is zero when it
 *     is not is a lie in the direction that matters.
 *   - Solo mining is a long shot by design, and the copy says so plainly beneath a
 *     figure that is otherwise easy to over-read.
 *   - Stale difficulty is labelled stale. A day-old reading is never presented as live.
 *   - When difficulty is unavailable the odds are absent, with a retry. Nothing is
 *     estimated, carried over from a previous configuration, or invented.
 *   - `oneInChance: null` omits that stat rather than printing "1 in Infinity".
 *
 * The completed-shares framing is deliberately absent: PowerPool has not supplied the
 * share-rate model, and inventing one would put a fabricated figure beside a truthful one.
 */

interface OddsDisplayProps {
  state: OddsState;
  onRetry: () => void;
  /** Null while still loading. Absent or unavailable simply renders no figure. */
  winningValue?: WinningValue | null;
}

export default function OddsDisplay({ state, onRetry, winningValue = null }: OddsDisplayProps) {
  const oneIn = state.status === 'ready' ? formatOneInChance(state.odds.oneInChance) : null;

  return (
    <section
      aria-labelledby="odds-heading"
      data-testid="odds-display"
      className="mm-rail-card"
    >
      <h2 id="odds-heading" className="mm-rail-heading">
        Your chance of finding a block
      </h2>

      {state.status === 'idle' && (
        <p className="mm-help">Choose a hashrate to see the odds.</p>
      )}

      {/*
        THE LOADING STATE IS THE READY STATE WITH THE NUMBERS MISSING.

        It used to be a 48px bar and one line of text, against a ready state of a 3.25rem
        hero, a subtitle, two stats and a paragraph -- so this card shrank by well over a
        hundred pixels on every re-quote, and the whole rail below it moved. The tree is
        now identical in both states and only the figures are withheld.

        NOTHING STALE IS SHOWN. No previous probability is remembered; a figure that no
        longer describes the configuration on screen is absent, not carried over.
      */}
      {state.status === 'loading' && (
        <div>
          <p className="mm-odds-hero">
            <PendingValue width="7ch" />
          </p>
          <p className="mm-help mt-1">chance of finding a block during your rental</p>

          <p className="mm-help mt-2">
            Put another way: <PendingValue width="5ch" />
          </p>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">

            {winningValue?.available && (
              <div className="mm-stat">
                <span className="mm-stat-label">Winning Value</span>
                <span className="mm-stat-value">
                  {formatCents(winningValue.winningValueCents, winningValue.currency)}
                </span>
              </div>
            )}
          </div>

          <p className="mm-help mt-4 text-xs">
            Solo mining is a long shot, and most attempts earn nothing, this is not a
            prediction of earnings.
          </p>

          {winningValue?.available && (
            <p className="mm-help mt-2 text-xs">
              Winning Value reflects the current value of BTC block reward before applicable
              fees, not expected earnings.
            </p>
          )}

          {/* The pending state in words. The placeholders above are decorative. */}
          <span className="sr-only" role="status">
            Calculating from live network difficulty…
          </span>
        </div>
      )}

      {state.status === 'unavailable' && (
        <div role="status" data-testid="odds-unavailable">
          <p className="mm-note mm-note--pending">
            <span>
              Live network difficulty isn&apos;t reachable right now, so we can&apos;t show your
              odds. We won&apos;t guess at them.
            </span>
          </p>
          <button type="button" onClick={onRetry} className="mm-btn-secondary mt-3">
            Try again
          </button>
        </div>
      )}

      {state.status === 'ready' && (
        <div>
          {/*
            "1 IN N" LEADS, AT THE PRODUCT OWNER'S REQUEST.

            Both figures are the same authoritative number from the odds engine, and
            neither is rounded or recomputed here. What changed is which one a customer
            reads first: a very small percentage is easy to skim past, while "1 in 2,085"
            is a shape people already understand. The percentage is kept, immediately
            beneath, because it is the precise form and some customers want it.

            The hero falls back to the percentage when the engine reports no "1 in N" --
            it returns null when the probability underflows -- so the panel never leads
            with an empty space.
          */}
          <p className="mm-odds-hero" data-testid="odds-one-in">
            {oneIn ?? formatProbabilityPercent(state.odds.probability)}
          </p>
          <p className="mm-help mt-1">chance of finding a block during your rental</p>

          {oneIn && (
            <p className="mm-help mt-2" data-testid="odds-probability">
              Put another way: {formatProbabilityPercent(state.odds.probability)}
            </p>
          )}

          <div className="mt-4 grid gap-2 sm:grid-cols-2">

            {/*
              WINNING VALUE REPLACED "STATISTICALLY EXPECTED BLOCKS" at the owner's
              request. The row it replaced showed the Poisson lambda (0.00048), which is
              the same information the hero percentage and "1 in N" already carry, in the
              one form a customer cannot read.

              WHAT THIS FIGURE IS: the PRIZE -- what a block pays if you find one. It is
              NOT an expected return, and nothing multiplies it by the probability. That
              product would be a projected yield, which this product may not publish.

              WHY THE SWAP IS SAFE: the panel still leads with the probability, and "1 in
              N" is still beside this. The pairing is the honest framing -- here is the
              prize, and here is how unlikely you are to win it. This figure alone would
              not be.

              It disappears entirely when the price is unavailable. There is no fallback.
            */}
            {winningValue?.available && (
              <div className="mm-stat">
                <span className="mm-stat-label">Winning Value</span>
                <span className="mm-stat-value" data-testid="odds-winning-value">
                  {formatCents(winningValue.winningValueCents, winningValue.currency)}
                </span>
              </div>
            )}
          </div>

          <p className="mm-help mt-4 text-xs">
            Solo mining is a long shot, and most attempts earn nothing, this is not a
            prediction of earnings.
          </p>

          {/*
            THE QUALIFIER IS NOT OPTIONAL WHERE A PRIZE IS NAMED.

            The subsidy is only part of a real block reward, and it errs in both
            directions: a block also pays its transaction fees (so the figure is low),
            while the customer receives it less PowerPool's pool fee and the network fee
            (so the figure is high). Neither is estimated. The hedge is the same wording
            Sidney approved for True Solo -- honest without inventing a number.
          */}
          {winningValue?.available && (
            <p className="mm-help mt-2 text-xs" data-testid="winning-value-note">
              Winning Value reflects the current value of BTC block reward before applicable
              fees, not expected earnings.
            </p>
          )}

          {/*
            TWO FOOTNOTES WERE REMOVED HERE, AND ONE WARNING WAS KEPT.

            Gone: "Bitcoin network difficulty changes over time, so these odds change
            with it", and the routine provenance line "Based on current network
            difficulty, updated N ago". Both were struck in a review pass -- they sat
            under a panel whose own copy already says solo mining is a long shot, and
            three qualifying sentences in a row read as hedging rather than honesty.

            KEPT: the STALE branch below. It is not a footnote, it is a warning, and it
            appears only when difficulty could not be refreshed. Removing it would leave
            a figure computed from an old reading presented as though it were current,
            which is the one thing the odds display must never do (docs/HANDOVER.md
            section 5: staleness is never hidden). It renders nothing at all in the
            normal case, so it costs the tidied layout nothing.
          */}
          {state.odds.difficultySource.status === 'stale' && (
            <p
              className="mt-3 text-xs"
              data-testid="odds-provenance"
              style={{ color: 'var(--mm-warning)' }}
            >
              Based on the last network difficulty we could retrieve
              {formatAge(state.odds.difficultySource.retrievedAt)
                ? ` (${formatAge(state.odds.difficultySource.retrievedAt)})`
                : ''}
              , not a live reading.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
